import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Tooltip } from '../Tooltip';
import { Spinner } from '../Spinner';
import type { ButtonProps } from './Button.types';
import type { Theme } from '../../theme';

// Spinner size mapping for button sizes
const spinnerSizeMap: Record<NonNullable<ButtonProps['size']>, 'xs' | 'sm'> = {
  xs: 'xs',
  sm: 'xs',
  md: 'sm',
  lg: 'sm',
};

/**
 * Size mappings for different button sizes
 * Uses theme typography for font sizes to support accessibility scaling
 * Heights aligned with Input, Select, and Multiselect components:
 * - sm: 32px
 * - md: 36px
 * - lg: 48px
 */
const getSizeStyles = (size: NonNullable<ButtonProps['size']>, theme: Theme) => {
  const { fontSize } = theme.typography;
  
  switch (size) {
    case 'xs':
      return css`
        min-width: 24px;
        min-height: 24px;
        padding: 0 4px;
        font-size: ${fontSize.xs};
      `;
    case 'sm':
      return css`
        height: 32px;
        padding: 4px 8px;
        font-size: ${fontSize.xs};
      `;
    case 'md':
      return css`
        height: 36px;
        padding: 8px 12px;
        font-size: ${fontSize.base};
      `;
    case 'lg':
      return css`
        height: 48px;
        padding: 12px 16px;
        font-size: ${fontSize.lg};
      `;
  }
};

/**
 * Styled Button component with ib-ui aligned styles
 */
const StyledButton = styled.button<{
  $variant: NonNullable<ButtonProps['variant']>;
  $size: NonNullable<ButtonProps['size']>;
  $fullWidth: boolean;
  $loading: boolean;
  $active: boolean;
  $width?: number;
  $hasLeftIcon: boolean;
  $hasRightIcon: boolean;
  $iconButton: boolean;
  $showHelpCursor: boolean;
  $fontWeight: 'bold' | 'normal';
  $outlineColor?: string;
  $backgroundColor?: string;
  $textColor?: string;
}>`
  /* Base styles */
  box-sizing: border-box;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borders.radius.sm};
  border: 1px solid transparent;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: ${({ theme, $fontWeight }) =>
    $fontWeight === 'normal' ? theme.typography.fontWeight.regular : theme.typography.fontWeight.semibold};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  white-space: nowrap;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, opacity 0.2s ease;

  /* Cursor */
  cursor: ${({ disabled, $loading, $showHelpCursor }) => {
    if (disabled || $loading) return 'not-allowed';
    if ($showHelpCursor) return 'help';
    return 'pointer';
  }};

  /* Width handling */
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  ${({ $width }) => $width && `width: ${$width}px;`}

  /* Size styles - uses theme typography for font sizes to support accessibility scaling */
  ${({ $size, theme }) => getSizeStyles($size, theme)}

  /* Adjust padding and dimensions for icon-only buttons (square) and icon positioning */
  ${({ $size, $iconButton, $hasLeftIcon, $hasRightIcon }) => {
    if ($size === 'xs') {
      if ($iconButton) return 'width: 24px; height: 24px; padding: 0;';
      if ($hasLeftIcon && !$hasRightIcon) return 'padding: 0 4px 0 1px;';
      if ($hasRightIcon && !$hasLeftIcon) return 'padding: 0 1px 0 4px;';
      if ($hasLeftIcon && $hasRightIcon) return 'padding: 0 1px;';
    }
    if ($size === 'sm') {
      if ($iconButton) return 'width: 32px; height: 32px; padding: 0;';
      if ($hasLeftIcon && !$hasRightIcon) return 'padding: 4px 8px 4px 4px;';
      if ($hasRightIcon && !$hasLeftIcon) return 'padding: 4px 4px 4px 8px;';
      if ($hasLeftIcon && $hasRightIcon) return 'padding: 4px 4px;';
    }
    if ($size === 'md') {
      if ($iconButton) return 'width: 36px; height: 36px; padding: 0;';
      if ($hasLeftIcon && !$hasRightIcon) return 'padding: 8px 12px 8px 7px;';
      if ($hasRightIcon && !$hasLeftIcon) return 'padding: 8px 7px 8px 12px;';
      if ($hasLeftIcon && $hasRightIcon) return 'padding: 8px 8px 8px 7px;';
    }
    if ($size === 'lg') {
      if ($iconButton) return 'width: 48px; height: 48px; padding: 0;';
      if ($hasLeftIcon && !$hasRightIcon) return 'padding: 12px 16px 12px 10px;';
      if ($hasRightIcon && !$hasLeftIcon) return 'padding: 12px 10px 12px 16px;';
      if ($hasLeftIcon && $hasRightIcon) return 'padding: 12px 10px;';
    }
    return '';
  }}

  /* Color variants */
  ${({ $variant, $active, $outlineColor, $backgroundColor, $textColor, theme }) => {
    const transparentBlack05 = 'rgba(0, 0, 0, 0.05)';
    const transparentBlack08 = 'rgba(0, 0, 0, 0.08)';
    const transparentBlack20 = 'rgba(0, 0, 0, 0.20)';
    const transparentBlack50 = 'rgba(0, 0, 0, 0.50)';
    const transparentWhite20 = 'rgba(255, 255, 255, 0.20)';
    const grey600 = theme.colors.grey[600];
    const grey700 = theme.colors.grey[700];
    const greenBase = theme.colors.semantic.green.base;
    const redBase = theme.colors.semantic.red.base;
    const orangeBase = theme.colors.semantic.orange.base;
    const highlightColor = theme.colors.highlight;
    const highlightText = theme.colors.highlightText;
    const white = theme.colors.white;

    switch ($variant) {
      case 'primary':
        return css`
          background-color: ${$backgroundColor || highlightColor};
          border-color: ${$backgroundColor || highlightColor};
          color: ${$textColor || highlightText};

          &:hover:not([disabled]) {
            position: relative;
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: ${transparentBlack05};
              border-radius: 4px;
              pointer-events: none;
            }
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'primaryInverse':
        return css`
          background-color: transparent;
          border-color: ${white};
          color: ${white};

          &:hover:not([disabled]) {
            border-color: ${white}80;
          }

          &:disabled {
            border-color: ${transparentWhite20};
            opacity: 0.5;
          }
        `;

      case 'secondary':
        if ($active) {
          return css`
            background-color: ${transparentBlack05};
            border-color: ${transparentBlack05};
            color: ${grey600};

            &:hover:not([disabled]) {
              background-color: ${transparentBlack08};
              border-color: transparent;
              color: ${grey700};
            }
          `;
        }
        return css`
          background-color: transparent;
          border-color: ${transparentBlack20};
          color: ${grey600};

          &:hover:not([disabled]) {
            border-color: ${transparentBlack50};
            color: ${grey700};
          }

          &:active:not([disabled]) {
            background-color: ${transparentBlack05};
            border-color: ${transparentBlack05};
          }

          &:disabled {
            border-color: ${transparentBlack20};
            opacity: 0.5;
          }
        `;

      case 'secondaryInverse':
        return css`
          background-color: transparent;
          border-color: ${grey600};
          color: ${grey600};

          &:hover:not([disabled]) {
            border-color: ${grey600}80;
          }

          &:disabled {
            border-color: ${transparentWhite20};
            opacity: 0.5;
          }
        `;

      case 'ghost':
        if ($active) {
          return css`
            background-color: ${transparentBlack05};
            border-color: transparent;
            color: ${grey600};

            &:hover:not([disabled]) {
              background-color: ${transparentBlack08};
              color: ${grey700};
            }
          `;
        }
        return css`
          background-color: transparent;
          border-color: transparent;
          color: ${grey600};

          &:hover:not([disabled]) {
            background-color: ${transparentBlack05};
            color: ${grey700};
          }

          &:active:not([disabled]) {
            background-color: ${transparentBlack05};
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'ghostInverse':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: ${white};

          &:hover:not([disabled]) {
            border-color: ${white}80;
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'ghostDanger':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: ${grey600};

          &:hover:not([disabled]) {
            background-color: ${theme.colors.semantic.red.lighter};
            color: ${redBase};
          }

          &:active:not([disabled]) {
            background-color: ${theme.colors.semantic.red.light};
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'link':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: ${theme.colors.semantic.blue.link};
          text-decoration: underline;
          padding: 0 4px;
          height: auto;
          min-height: auto;
          font-weight: ${theme.typography.fontWeight.regular};

          &:hover:not([disabled]) {
            color: ${theme.colors.semantic.blue.dark};
          }

          &:active:not([disabled]) {
            color: ${theme.colors.semantic.blue.darker};
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'positive':
        return css`
          background-color: ${$backgroundColor || greenBase};
          border-color: ${$backgroundColor || greenBase};
          color: ${$textColor || white};

          &:hover:not([disabled]) {
            position: relative;
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: ${transparentBlack05};
              border-radius: 4px;
              pointer-events: none;
            }
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'danger':
      case 'destructive':
        return css`
          background-color: ${$backgroundColor || redBase};
          border-color: ${$backgroundColor || redBase};
          color: ${$textColor || white};

          &:hover:not([disabled]) {
            position: relative;
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: ${transparentBlack05};
              border-radius: 4px;
              pointer-events: none;
            }
          }

          &:disabled {
            opacity: 0.5;
          }
        `;

      case 'outlinePrimary':
        return css`
          background-color: transparent;
          border-color: ${white};
          color: ${white};

          &:hover:not([disabled]) {
            border-color: ${white}80;
          }

          &:disabled {
            border-color: ${transparentWhite20};
            opacity: 0.5;
          }
        `;

      case 'outlineSecondary':
        return css`
          background-color: transparent;
          border-color: ${$outlineColor || grey600};
          color: ${$outlineColor || grey600};

          &:hover:not([disabled]) {
            border-color: ${$outlineColor || grey600}80;
          }

          &:disabled {
            border-color: ${transparentBlack20};
            opacity: 0.5;
          }
        `;

      case 'outlineSuccess':
        return css`
          background-color: transparent;
          border-color: ${$outlineColor || greenBase};
          color: ${$outlineColor || greenBase};

          &:hover:not([disabled]) {
            border-color: ${$outlineColor || greenBase}80;
          }

          &:disabled {
            border-color: ${transparentBlack20};
            opacity: 0.5;
          }
        `;

      case 'outlineError':
      case 'outlineDanger':
        return css`
          background-color: transparent;
          border-color: ${$outlineColor || redBase};
          color: ${$outlineColor || redBase};

          &:hover:not([disabled]) {
            border-color: ${$outlineColor || redBase}80;
          }

          &:disabled {
            border-color: ${transparentBlack20};
            opacity: 0.5;
          }
        `;

      case 'outlineWarning':
        return css`
          background-color: transparent;
          border-color: ${$outlineColor || orangeBase};
          color: ${$outlineColor || orangeBase};

          &:hover:not([disabled]) {
            border-color: ${$outlineColor || orangeBase}80;
          }

          &:disabled {
            border-color: ${transparentBlack20};
            opacity: 0.5;
          }
        `;

      default:
        return css`
          background-color: ${$backgroundColor || highlightColor};
          border-color: ${$backgroundColor || highlightColor};
          color: ${$textColor || highlightText};

          &:disabled {
            opacity: 0.5;
          }
        `;
    }
  }}

  /* Focus state */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlight};
    outline-offset: 2px;
  }

  /* Loading state pointer events */
  ${({ $loading }) =>
    $loading &&
    css`
      pointer-events: none;
    `}
`;

/**
 * Wrapper for positioning the Spinner component in loading state
 */
const SpinnerWrapper = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Content wrapper - hidden when loading
 */
const Content = styled.span<{ $loading: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  visibility: ${({ $loading }) => ($loading ? 'hidden' : 'visible')};

  /* Text truncation */
  .button-label {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  > svg {
    flex-shrink: 0;
  }
`;

/**
 * Icon wrapper
 */
const IconWrapper = styled.span<{ $size: NonNullable<ButtonProps['size']> }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    ${({ $size }) => {
      const iconSize = $size === 'xs' ? 16 : 24;
      return css`
        width: ${iconSize}px;
        height: ${iconSize}px;
      `;
    }}
  }
`;

/**
 * Button component - A versatile button for user interactions
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Button variant="primary" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * // With loading state
 * <Button variant="secondary" size="lg" loading>
 *   Loading...
 * </Button>
 *
 * // With icons
 * <Button variant="ghost" leftIcon={<Icon name="search" />}>
 *   Search
 * </Button>
 *
 * // With tooltip
 * <Button variant="primary" tooltip="Save your changes">
 *   Save
 * </Button>
 *
 * // New variants from ib-ui
 * <Button variant="positive">Success</Button>
 * <Button variant="destructive">Delete</Button>
 * <Button variant="primaryInverse">Inverse</Button>
 *
 * // Active state for toggles
 * <Button variant="ghost" active={isActive}>
 *   Toggle
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      disabled = false,
      loading = false,
      icon,
      leftIcon,
      rightIcon,
      type = 'button',
      onClick,
      className,
      active = false,
      tooltip,
      tooltipProps,
      width,
      showHelpCursor = false,
      fontWeight = 'bold',
      outlineColor,
      backgroundColor,
      textColor,
      'aria-label': ariaLabel,
      'data-testid': testId,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const effectiveLeftIcon = icon || leftIcon;
    const hasLeftIcon = !!effectiveLeftIcon;
    const hasRightIcon = !!rightIcon;
    const iconButton = !children && (hasLeftIcon || hasRightIcon);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }
      event.currentTarget.blur();
      onClick?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isDisabled && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        return;
      }
      if (event.key === 'Escape') {
        event.currentTarget.blur();
      }
    };

    const buttonElement = (
      <StyledButton
        ref={ref}
        type={type}
        className={className}
        disabled={isDisabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-disabled={isDisabled}
        aria-busy={loading}
        aria-pressed={active}
        data-testid={dataTestId || testId || `button-${variant}`}
        data-id={dataId}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $loading={loading}
        $active={active}
        $width={width}
        $hasLeftIcon={hasLeftIcon}
        $hasRightIcon={hasRightIcon}
        $iconButton={iconButton}
        $showHelpCursor={showHelpCursor}
        $fontWeight={fontWeight}
        $outlineColor={outlineColor}
        $backgroundColor={backgroundColor}
        $textColor={textColor}
        {...rest}
      >
        {loading && (
          <SpinnerWrapper aria-hidden="true">
            <Spinner size={spinnerSizeMap[size]} color="inherit" smooth />
          </SpinnerWrapper>
        )}
        <Content $loading={loading}>
          {effectiveLeftIcon && (
            <IconWrapper aria-hidden="true" $size={size}>
              {effectiveLeftIcon}
            </IconWrapper>
          )}
          {children && <span className="button-label">{children}</span>}
          {rightIcon && (
            <IconWrapper aria-hidden="true" $size={size}>
              {rightIcon}
            </IconWrapper>
          )}
        </Content>
      </StyledButton>
    );

    if (tooltip) {
      return (
        <Tooltip
          content={tooltip}
          position={tooltipProps?.position || 'bottom'}
          delay={tooltipProps?.delay}
          hideDelay={tooltipProps?.hideDelay}
          maxWidth={tooltipProps?.maxWidth}
          zIndex={tooltipProps?.zIndex}
        >
          {buttonElement}
        </Tooltip>
      );
    }

    return buttonElement;
  }
);

Button.displayName = 'Button';

export default Button;
